import React, {
  useRef,
  useEffect,
  useMemo,
  useCallback,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { Col, FormInstance, Grid, Row, Schema, Text } from 'rsuite';
import { FormModelPerson, Person } from 'src/types/person';
import {
  getInitialFormModelValues,
  mapFormModelToPerson,
} from 'src/common/people';
import { useSelector } from 'react-redux';
import { selectConfigExpressRegistrantOptions } from 'src/store/slices/config/configSelectors';
import { ApplicationData, REGISTRANT_OPTIONS } from 'src/types/flow';
import {
  LOCAL_CONTACT,
  PROPERTY_MANAGEMENT_OPTIONS,
} from 'src/components/payment/constants';
import { useUserRole } from 'src/hooks/useUserRole';
import PersonDetails from '../PersonDetails';
import { formValuePersonSchema } from '../Owners/utils';
import {
  emailRequiredValidation,
  uniqueEmailValidation,
} from 'src/common/validation';
import { LocalContactRadioGroup } from './LocalContactRadioGroup';
import { PropertyManagerRadioGroup } from './PropertyManagerRadioGroup';
import {
  getOwnerRepValidationModel,
  getPropertyManagerValidationModel,
  SELECTED_CONTACT_PROXIMITY_MESSAGE,
} from './validations';
import {
  ExpressRegistrationRegistrant,
  PersonOptionalFields,
} from 'src/types/configuration';
import { FLOWS_ENUM } from 'src/common/enums';
import { lookupAddress } from 'src/api/apiAccess';
import { LookupAddressResponse } from 'src/types/address';
import { CustomHTML } from 'src/components/common/CustomHTML';

interface ContactPersonData {
  description: string;
  enabled: boolean;
  optionalFields: PersonOptionalFields;
  verbiage?: string;
}

interface ContactFormProps {
  data: ApplicationData;
  flow: FLOWS_ENUM;
  localContactData: ContactPersonData;
  propertyManagerData: ContactPersonData;
}

export interface ContactFormRef {
  getValues: () => ApplicationData;
  validateForm: () => Promise<boolean>;
}

export const ContactForm = forwardRef<ContactFormRef, ContactFormProps>(
  (
    { data, localContactData, propertyManagerData, flow }: ContactFormProps,
    ref
  ) => {
    const isAdminUpdate = flow === FLOWS_ENUM.ADMIN_UPDATE;
    const propertyManagerForm = useRef<FormInstance<FormModelPerson>>(null);
    const ownerRepForm = useRef<FormInstance<FormModelPerson>>(null);
    const [propertyManagerFormValues, setPropertyManagerFormValues] =
      useState<FormModelPerson>(getInitialFormModelValues());
    const [ownerRepFormValues, setOwnerRepFormValues] =
      useState<FormModelPerson>(getInitialFormModelValues());
    const [localContactError, setLocalContactError] = useState<string>();
    const [physicalAddressProximityError, setPhysicalAddressProximityError] =
      useState<string | undefined>();
    const [selectedContact, setSelectedContact] = useState(LOCAL_CONTACT.OWNER);
    const [selectedManager, setSelectedManager] = useState(
      PROPERTY_MANAGEMENT_OPTIONS.SELF_MANAGED
    );

    const handleChangeManager = useCallback(
      (value: PROPERTY_MANAGEMENT_OPTIONS) => {
        setSelectedManager(value);
        if (
          value === PROPERTY_MANAGEMENT_OPTIONS.SELF_MANAGED &&
          selectedContact === LOCAL_CONTACT.PROPERTY_MANAGER
        ) {
          setSelectedContact(LOCAL_CONTACT.OWNER);
        }
      },
      [selectedContact]
    );

    const handleChangeContact = useCallback((value: LOCAL_CONTACT) => {
      setSelectedContact(value);
      setLocalContactError(undefined);
      setPhysicalAddressProximityError(undefined);
    }, []);

    const { contacts, registrant, property, tenant } = data;

    const selectedRegistrantOptions = useSelector(
      selectConfigExpressRegistrantOptions
    );
    const {
      isPropertyManager,
      isOwner,
      isTenant,
      isRegistrant,
      isRegistrantTenant,
    } = useUserRole(registrant, contacts, tenant);

    const getPropertyManager = useCallback((): Person => {
      // THe property manager here comes from the previous page? What if we select the property manager from this one?

      if (selectedManager === PROPERTY_MANAGEMENT_OPTIONS.SELF_MANAGED) {
        if (isOwner) {
          return { ...(registrant?.owner[0] as Person) };
        }
        if (
          data.registrant?.registrantType ===
            REGISTRANT_OPTIONS.PROPERTY_MANAGER_INDEPENDENT ||
          data.registrant?.registrantType ===
            REGISTRANT_OPTIONS.PROPERTY_MANAGER
        ) {
          return data.registrant.registrant;
        }

        return { ...(contacts?.propertyManager as Person) };
      } else if (selectedManager === PROPERTY_MANAGEMENT_OPTIONS.TENANT) {
        return { ...(tenant as Person) };
      }
      return { ...propertyManagerFormValues };
    }, [
      contacts,
      data,
      isOwner,
      propertyManagerFormValues,
      registrant,
      selectedManager,
      tenant,
    ]);

    useImperativeHandle(ref, () => ({
      validateForm: async () => {
        if (
          selectedContact === LOCAL_CONTACT.OTHER &&
          !ownerRepForm.current?.check()
        ) {
          console.error('Local Contact Form is invalid');
          return false;
        } else if (physicalAddressRequired) {
          const allowedZipCodes: number[] =
            localContactData.optionalFields.physical_address
              ?.allowed_postcodes ?? [];
          let selectedPerson: FormModelPerson | undefined;
          if (selectedContact === LOCAL_CONTACT.OWNER) {
            selectedPerson = registrant?.owner?.[0] as Person | undefined;
          } else if (selectedContact === LOCAL_CONTACT.PROPERTY_MANAGER) {
            selectedPerson = getPropertyManager();
          }
          if (selectedContact !== LOCAL_CONTACT.OTHER) {
            let zipcode =
              selectedPerson?.completePhysicalAddress?.components?.zipcode;
            if (
              zipcode === undefined &&
              selectedContact === LOCAL_CONTACT.OWNER
            ) {
              const mailingAddress = selectedPerson?.mailingAddress;
              if (mailingAddress) {
                const { data } = (await lookupAddress(mailingAddress)) as {
                  data: LookupAddressResponse;
                };
                zipcode = data?.suggestions?.[0]?.zipcode;
              }
            }
            if (
              zipcode === undefined ||
              !allowedZipCodes.includes(Number(zipcode))
            ) {
              setPhysicalAddressProximityError(
                SELECTED_CONTACT_PROXIMITY_MESSAGE
              );
              return false;
            }
          }
          setPhysicalAddressProximityError(undefined);
        }

        if (
          selectedManager === PROPERTY_MANAGEMENT_OPTIONS.SELF_MANAGED &&
          selectedContact === LOCAL_CONTACT.PROPERTY_MANAGER &&
          !isPropertyManager
        ) {
          setLocalContactError('Please select a local contact.');
          return false;
        }

        if (showPropertyManagerQuestion) {
          // The form should be evaulated just when the form is present,
          // otherwise it will error
          if (isAdminUpdate || !isPropertyManager || isOwner) {
            if (
              selectedManager ===
                PROPERTY_MANAGEMENT_OPTIONS.PROPERTY_MANAGER &&
              !propertyManagerForm.current?.check()
            ) {
              console.error(
                'Property Manager Form is invalid',
                selectedManager
              );
              return false;
            }
          }
        }
        return true;
      },
      getValues: (): ApplicationData => {
        const identifiedPropertyManager: Person = getPropertyManager();
        let localContact: FormModelPerson;

        switch (selectedContact) {
          case LOCAL_CONTACT.PROPERTY_MANAGER:
            localContact = { ...identifiedPropertyManager };
            break;
          case LOCAL_CONTACT.TENANT:
            localContact = { ...(tenant as Person) };
            break;
          case LOCAL_CONTACT.OWNER:
          default:
            localContact = { ...(registrant?.owner?.[0] as Person) };
            break;
          case LOCAL_CONTACT.OTHER:
            localContact = { ...ownerRepFormValues };
            break;
        }

        return {
          contacts: {
            selectedManagementOption: selectedManager,
            selectedOwnerRepOption: selectedContact,
            propertyManager: mapFormModelToPerson(identifiedPropertyManager),
            localContact: mapFormModelToPerson(localContact),
          },
        } as ApplicationData;
      },
    }));

    const getSameAsPropertyAddress = useCallback(
      (person: Person) => {
        return Boolean(
          person.mailingAddress &&
            person.mailingAddress.toLowerCase() ===
              property?.propertyaddress.toLowerCase()
        );
      },
      [property]
    );

    useEffect(() => {
      // Initial Values of the Form
      if (contacts !== undefined) {
        // Management Radio Option
        setSelectedManager(
          (prev) => contacts?.selectedManagementOption ?? prev
        );
        // Selected Contact
        setSelectedContact((prev) => contacts?.selectedOwnerRepOption ?? prev);

        // Values of Local Contact if Selection is OTHER
        if (contacts.selectedOwnerRepOption === LOCAL_CONTACT.OTHER) {
          const person = contacts.localContact as Person;
          setOwnerRepFormValues({
            ...person,
            sameAsPropertyAddress: getSameAsPropertyAddress(person),
          });
        }
        // Values of Property Manager if Selection is PROPERTY_MANAGER
        if (
          contacts.selectedManagementOption ===
          PROPERTY_MANAGEMENT_OPTIONS.PROPERTY_MANAGER
        ) {
          const person = contacts.propertyManager as Person;
          setPropertyManagerFormValues({
            ...person,
            sameAsPropertyAddress: getSameAsPropertyAddress(person),
          });
        }
      } else {
        // Set defaults for first time form fill
        if (isPropertyManager) {
          setSelectedContact(LOCAL_CONTACT.PROPERTY_MANAGER);
          // setSelectedManager(PROPERTY_MANAGEMENT_OPTIONS.PROPERTY_MANAGER);
        } else {
          setSelectedContact(LOCAL_CONTACT.OWNER);
        }
      }
    }, [contacts, isPropertyManager, getSameAsPropertyAddress]);

    // When registrantType is not yet set (e.g. first visit in APPLY flow before
    // completing the registrant step), treat as unconstrained: show the section,
    // allow changes, and show "Myself" in the local contact list. Once registrant
    // data exists the normal isOwner / isRegistrant guards apply.
    const registrantNotYetSet = useMemo(
      () => !registrant?.registrantType,
      [registrant]
    );

    const showPropertyManagerQuestion = useMemo(() => {
      if (flow === FLOWS_ENUM.APPLY_LICENSE) {
        return (
          propertyManagerData.enabled &&
          (!registrant?.registrantType ||
            registrant?.registrantType === REGISTRANT_OPTIONS.OWNER ||
            registrant?.registrantType === REGISTRANT_OPTIONS.TENANT)
        );
      }
      return (
        isAdminUpdate ||
        (propertyManagerData.enabled &&
          (!registrant?.registrantType || isPropertyManager || !!isOwner))
      );
    }, [
      propertyManagerData,
      flow,
      isOwner,
      isPropertyManager,
      isAdminUpdate,
      registrant,
    ]);

    const canChangePropertyManager = useMemo(() => {
      return registrantNotYetSet || (isOwner && isRegistrant) || isAdminUpdate;
    }, [isRegistrant, isOwner, isAdminUpdate, registrantNotYetSet]);

    const canChangePropertyManagerDetails = useMemo(() => {
      return registrantNotYetSet || isRegistrant || isAdminUpdate;
    }, [isRegistrant, isAdminUpdate, registrantNotYetSet]);

    const pmExcludedEmails = useMemo(() => {
      const emails: string[] = (registrant?.owner ?? []).map(
        (o) => (o as Person).email!
      );
      if (registrant?.registrant?.email) {
        emails.push(registrant.registrant.email);
      }
      return emails;
    }, [registrant]);

    const localContactExcludedEmails = useMemo(() => {
      const extra: string[] = [];
      if (selectedManager === PROPERTY_MANAGEMENT_OPTIONS.PROPERTY_MANAGER) {
        if (propertyManagerFormValues.email) {
          extra.push(propertyManagerFormValues.email);
        }
      } else if (selectedManager === PROPERTY_MANAGEMENT_OPTIONS.TENANT) {
        if ((tenant as Person)?.email) {
          extra.push((tenant as Person).email!);
        }
      }
      return [...pmExcludedEmails, ...extra];
    }, [
      pmExcludedEmails,
      selectedManager,
      propertyManagerFormValues.email,
      tenant,
    ]);

    const propertyManagerFormModel = useMemo(() => {
      const emailRule = pmExcludedEmails.length
        ? uniqueEmailValidation(pmExcludedEmails)
        : emailRequiredValidation;
      return Schema.Model<FormModelPerson>({
        ...formValuePersonSchema,
        email: emailRule,
      });
    }, [pmExcludedEmails]);

    const propertyManagerDynamicValidationModel = useMemo(() => {
      const base = getPropertyManagerValidationModel(
        propertyManagerData.optionalFields
      );
      const emailRule = pmExcludedEmails.length
        ? uniqueEmailValidation(pmExcludedEmails)
        : base.email;
      return { ...base, email: emailRule };
    }, [pmExcludedEmails, propertyManagerData.optionalFields]);

    const physicalAddressRequired =
      !!localContactData.optionalFields.physical_address?.required;

    const ownerRepFormModel = useMemo(() => {
      const emailRule = localContactExcludedEmails.length
        ? uniqueEmailValidation(localContactExcludedEmails)
        : emailRequiredValidation;
      return Schema.Model<FormModelPerson>({
        ...formValuePersonSchema,
        email: emailRule,
      });
    }, [localContactExcludedEmails]);

    const ownerRepDynamicValidationModel = useMemo(() => {
      const base = getOwnerRepValidationModel(localContactData.optionalFields);
      const emailRule = localContactExcludedEmails.length
        ? uniqueEmailValidation(localContactExcludedEmails)
        : base.email;
      return { ...base, email: emailRule };
    }, [localContactExcludedEmails, localContactData.optionalFields]);

    return (
      <Grid
        className="mt-12 ml-0 "
        fluid
      >
        {showPropertyManagerQuestion && (
          <>
            <Row className="mb-16">
              <Col xs={24}>
                <Text
                  as="h5"
                  className="text-bold mb-8"
                >
                  Property Management Company
                </Text>
                <CustomHTML text={propertyManagerData.description} />
              </Col>
            </Row>
            <Row>
              <Col
                md={12}
                xs={24}
              >
                <PropertyManagerRadioGroup
                  configuration={
                    selectedRegistrantOptions as ExpressRegistrationRegistrant
                  }
                  disabled={!canChangePropertyManager}
                  isAdminUpdate={isAdminUpdate}
                  isOwner={isOwner}
                  isPropertyManager={isPropertyManager}
                  isRegistrantTenant={isRegistrantTenant}
                  onChange={handleChangeManager}
                  value={selectedManager}
                />
              </Col>
            </Row>
            {(!isPropertyManager || isOwner) &&
              selectedManager ===
                PROPERTY_MANAGEMENT_OPTIONS.PROPERTY_MANAGER && (
                <>
                  <Row className="mt-8 mb-8">
                    <Col
                      md={12}
                      xs={24}
                    >
                      <Text
                        as="h5"
                        style={{ fontWeight: 'bold' }}
                      >
                        Property Manager Information
                      </Text>
                    </Col>
                  </Row>
                  <Row>
                    <Col
                      md={12}
                      xs={24}
                    >
                      <PersonDetails
                        ref={propertyManagerForm}
                        formModel={propertyManagerFormModel}
                        formValues={propertyManagerFormValues}
                        propertyAddress={property?.propertyaddress}
                        validationModel={propertyManagerDynamicValidationModel}
                        setFormValues={setPropertyManagerFormValues}
                        hiddenFields={{
                          physicalAddress:
                            !propertyManagerData.optionalFields.physical_address
                              ?.required,
                        }}
                        optionalFields={
                          propertyManagerData.optionalFields as Record<
                            string,
                            'optional' | 'required'
                          >
                        }
                        disabled={!canChangePropertyManagerDetails}
                      />
                    </Col>
                  </Row>
                </>
              )}
          </>
        )}
        <Row
          className={`mb-16 ${registrant?.registrantType !== REGISTRANT_OPTIONS.PROPERTY_MANAGER && 'mt-24'}`}
        >
          <Col xs={24}>
            <Text
              as="h5"
              className="text-bold mb-8"
            >
              {localContactData.verbiage}
            </Text>
            <CustomHTML text={localContactData.description} />
          </Col>
        </Row>
        <Row>
          <Col
            md={12}
            xs={24}
          >
            <LocalContactRadioGroup
              error={localContactError}
              isOwner={registrantNotYetSet || isOwner}
              isTenant={isTenant}
              isPropertyManager={isPropertyManager}
              localContactVerbiage={localContactData.verbiage ?? ''}
              onChange={handleChangeContact}
              propertyManager={selectedManager}
              selectedError={physicalAddressProximityError}
              value={selectedContact}
            />
          </Col>
        </Row>
        {selectedContact === LOCAL_CONTACT.OTHER && (
          <>
            <Row className="mt-24 mb-8">
              <Col
                md={12}
                xs={24}
              >
                <Text
                  as="h5"
                  style={{ fontWeight: 'bold' }}
                >
                  {localContactData.verbiage} Information
                </Text>
              </Col>
            </Row>
            <Row>
              <Col
                md={12}
                xs={24}
              >
                <PersonDetails
                  ref={ownerRepForm}
                  formModel={ownerRepFormModel}
                  formValues={ownerRepFormValues}
                  hiddenFields={{ physicalAddress: !physicalAddressRequired }}
                  optionalFields={
                    localContactData.optionalFields as Record<
                      string,
                      'optional' | 'required'
                    >
                  }
                  propertyAddress={property?.propertyaddress}
                  setFormValues={setOwnerRepFormValues}
                  validationModel={ownerRepDynamicValidationModel}
                />
              </Col>
            </Row>
          </>
        )}
      </Grid>
    );
  }
);

ContactForm.displayName = 'ContactForm';
